// Curated vacation-rental amenities (Booking.com / Airbnb / Vrbo coverage),
// grouped for the property editor's amenity picker. Each amenity has a stable
// `key` (stored in properties.amenities_json as a JSON array), a Spanish `label`,
// and a lucide-react `icon` component.
import {
  Wifi, Wind, Flame, UtensilsCrossed, Refrigerator, Microwave, CoffeeIcon,
  WashingMachine, Tv, MonitorPlay, Waves, Bath, Trees, Sun, Beef, Car,
  ArrowUpDown, Baby, Dog, ShieldCheck, FireExtinguisher, Cross, Lock,
  Accessibility, BedDouble, Wind as Hairdryer, Shirt, Eye, ParkingCircle,
  Snowflake, Utensils, ConciergeBell, Sparkles, Cigarette, KeyRound,
  type LucideIcon,
} from 'lucide-react';

export type AmenityGroupKey =
  | 'essentials' | 'kitchen' | 'bathroom' | 'outdoor'
  | 'family' | 'safety' | 'accessibility' | 'entertainment' | 'services';

export interface Amenity {
  key: string;
  label: string;          // Spanish
  icon: LucideIcon;
  group: AmenityGroupKey;
}

// Spanish group titles for the picker headings.
export const AMENITY_GROUPS: { key: AmenityGroupKey; label: string }[] = [
  { key: 'essentials', label: 'Esenciales' },
  { key: 'kitchen', label: 'Cocina' },
  { key: 'bathroom', label: 'Baño' },
  { key: 'outdoor', label: 'Exterior' },
  { key: 'family', label: 'Familia' },
  { key: 'safety', label: 'Seguridad' },
  { key: 'accessibility', label: 'Accesibilidad' },
  { key: 'entertainment', label: 'Entretenimiento' },
  { key: 'services', label: 'Servicios' },
];

export const AMENITIES: Amenity[] = [
  // ── Essentials ──
  { key: 'wifi', label: 'WiFi', icon: Wifi, group: 'essentials' },
  { key: 'air_conditioning', label: 'Aire acondicionado', icon: Snowflake, group: 'essentials' },
  { key: 'heating', label: 'Calefacción', icon: Flame, group: 'essentials' },
  { key: 'tv', label: 'TV', icon: Tv, group: 'essentials' },
  { key: 'linens', label: 'Sábanas', icon: BedDouble, group: 'essentials' },
  { key: 'towels', label: 'Toallas', icon: Bath, group: 'essentials' },
  { key: 'iron', label: 'Plancha', icon: Shirt, group: 'essentials' },
  { key: 'washer', label: 'Lavadora', icon: WashingMachine, group: 'essentials' },
  { key: 'dryer', label: 'Secadora', icon: Wind, group: 'essentials' },

  // ── Kitchen ──
  { key: 'kitchen', label: 'Cocina', icon: UtensilsCrossed, group: 'kitchen' },
  { key: 'dishwasher', label: 'Lavavajillas', icon: Utensils, group: 'kitchen' },
  { key: 'fridge', label: 'Nevera', icon: Refrigerator, group: 'kitchen' },
  { key: 'microwave', label: 'Microondas', icon: Microwave, group: 'kitchen' },
  { key: 'coffee_maker', label: 'Cafetera', icon: CoffeeIcon, group: 'kitchen' },

  // ── Bathroom ──
  { key: 'hairdryer', label: 'Secador de pelo', icon: Hairdryer, group: 'bathroom' },
  { key: 'bathtub', label: 'Bañera', icon: Bath, group: 'bathroom' },
  { key: 'toiletries', label: 'Artículos de aseo', icon: Sparkles, group: 'bathroom' },

  // ── Outdoor ──
  { key: 'pool_private', label: 'Piscina privada', icon: Waves, group: 'outdoor' },
  { key: 'pool_shared', label: 'Piscina compartida', icon: Waves, group: 'outdoor' },
  { key: 'jacuzzi', label: 'Jacuzzi', icon: Bath, group: 'outdoor' },
  { key: 'terrace', label: 'Terraza / Balcón', icon: Sun, group: 'outdoor' },
  { key: 'garden', label: 'Jardín', icon: Trees, group: 'outdoor' },
  { key: 'bbq', label: 'Barbacoa', icon: Beef, group: 'outdoor' },
  { key: 'sea_view', label: 'Vistas al mar', icon: Eye, group: 'outdoor' },

  // ── Family ──
  { key: 'family_friendly', label: 'Apto para niños', icon: Baby, group: 'family' },
  { key: 'crib', label: 'Cuna', icon: BedDouble, group: 'family' },
  { key: 'pets_allowed', label: 'Se admiten mascotas', icon: Dog, group: 'family' },

  // ── Safety ──
  { key: 'smoke_detector', label: 'Detector de humo', icon: ShieldCheck, group: 'safety' },
  { key: 'fire_extinguisher', label: 'Extintor', icon: FireExtinguisher, group: 'safety' },
  { key: 'first_aid', label: 'Botiquín', icon: Cross, group: 'safety' },
  { key: 'safe', label: 'Caja fuerte', icon: Lock, group: 'safety' },
  { key: 'no_smoking', label: 'Prohibido fumar', icon: Cigarette, group: 'safety' },

  // ── Accessibility ──
  { key: 'wheelchair_accessible', label: 'Accesible silla de ruedas', icon: Accessibility, group: 'accessibility' },
  { key: 'elevator', label: 'Ascensor', icon: ArrowUpDown, group: 'accessibility' },

  // ── Entertainment ──
  { key: 'smart_tv', label: 'Smart TV / Netflix', icon: MonitorPlay, group: 'entertainment' },

  // ── Services ──
  { key: 'free_parking', label: 'Parking gratuito', icon: Car, group: 'services' },
  { key: 'paid_parking', label: 'Parking de pago', icon: ParkingCircle, group: 'services' },
  { key: 'self_checkin', label: 'Auto check-in', icon: KeyRound, group: 'services' },
  { key: 'breakfast', label: 'Desayuno', icon: ConciergeBell, group: 'services' },
];

const AMENITY_BY_KEY = new Map(AMENITIES.map(a => [a.key, a]));
export function amenityByKey(key: string): Amenity | undefined {
  return AMENITY_BY_KEY.get(key);
}

// Parse the stored JSON array of keys → string[] (tolerant of null/garbage).
export function parseAmenities(json: string | null | undefined): string[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}
